import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { categoryHierarchyLabel, type ChannelCategory } from "@/lib/channel-categories";
import { providerLabel } from "@/lib/listing-action";

const inputCls =
  "w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500";

type TaxonomyStatus = {
  ok?: boolean;
  provider?: string;
  count?: number;
  last_synced_at?: string | null;
  shared_cache?: boolean;
  unsupported?: boolean;
  error?: string;
};

type SearchPayload = { ok?: boolean; rows?: ChannelCategory[]; count?: number; error?: string };

function asCategories(rows: unknown): ChannelCategory[] {
  if (!Array.isArray(rows)) return [];
  return rows.map((row) => {
    const r = row as ChannelCategory;
    return {
      id: String(r.id),
      name: r.name,
      path: r.path ?? r.name,
      parentId: r.parentId ?? null,
      isLeaf: Boolean(r.isLeaf),
    };
  });
}

export function ChannelCategoryPicker(props: {
  orgId: string;
  connectorId: string;
  provider: string | null;
  selectedId: string;
  selectedName: string;
  confirmed: boolean;
  hints: string[];
  onSelect: (id: string, name: string) => void;
  onClear: () => void;
}) {
  const queryClient = useQueryClient();
  const [query, setQuery] = useState("");
  const [browse, setBrowse] = useState(false);
  const [browseStack, setBrowseStack] = useState<{ id: string | null; name: string }[]>([{ id: null, name: "Top" }]);
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState("");
  const provider = props.provider ?? "";
  const cacheReady = provider === "ebay" || provider === "bigcommerce";
  const browseParent = browseStack[browseStack.length - 1]?.id ?? null;

  const statusQuery = useQuery({
    queryKey: ["channel-taxonomy-status", props.orgId, props.connectorId],
    enabled: cacheReady && !!props.orgId && !!props.connectorId,
    queryFn: async (): Promise<TaxonomyStatus> => {
      const { data, error } = await supabase.rpc("search_channel_categories", {
        p_org_id: props.orgId,
        p_connector_id: props.connectorId,
        p_mode: "status",
      });
      if (error) throw new Error(error.message);
      return (data ?? {}) as TaxonomyStatus;
    },
  });

  const searchQuery = useQuery({
    queryKey: ["channel-taxonomy-search", props.orgId, props.connectorId, query.trim()],
    enabled: cacheReady && query.trim().length >= 2 && (statusQuery.data?.count ?? 0) > 0,
    queryFn: async (): Promise<ChannelCategory[]> => {
      const { data, error } = await supabase.rpc("search_channel_categories", {
        p_org_id: props.orgId,
        p_connector_id: props.connectorId,
        p_mode: "search",
        p_query: query.trim(),
        p_limit: 25,
      });
      if (error) throw new Error(error.message);
      return asCategories((data as SearchPayload)?.rows);
    },
  });

  const childrenQuery = useQuery({
    queryKey: ["channel-taxonomy-children", props.orgId, props.connectorId, browseParent],
    enabled: cacheReady && browse && (statusQuery.data?.count ?? 0) > 0,
    queryFn: async (): Promise<ChannelCategory[]> => {
      const { data, error } = await supabase.rpc("search_channel_categories", {
        p_org_id: props.orgId,
        p_connector_id: props.connectorId,
        p_mode: "children",
        p_parent_id: browseParent,
        p_limit: 80,
      });
      if (error) throw new Error(error.message);
      return asCategories((data as SearchPayload)?.rows);
    },
  });

  const hintQuery = useMemo(() => {
    const tokens = props.hints.join(" ").toLowerCase();
    if (tokens.includes("collar")) return "collar";
    if (tokens.includes("dog")) return "dog";
    const first = props.hints[0]?.trim();
    return first && first.length >= 3 ? first : "";
  }, [props.hints]);

  const hintResults = useQuery({
    queryKey: ["channel-taxonomy-hints", props.orgId, props.connectorId, hintQuery],
    enabled: cacheReady && hintQuery.length >= 3 && (statusQuery.data?.count ?? 0) > 0 && !query.trim(),
    queryFn: async (): Promise<ChannelCategory[]> => {
      const { data, error } = await supabase.rpc("search_channel_categories", {
        p_org_id: props.orgId,
        p_connector_id: props.connectorId,
        p_mode: "search",
        p_query: hintQuery,
        p_limit: 4,
      });
      if (error) throw new Error(error.message);
      return asCategories((data as SearchPayload)?.rows);
    },
  });

  const count = statusQuery.data?.count ?? 0;
  const empty = cacheReady && !statusQuery.isFetching && count === 0;
  const staleLabel = statusQuery.data?.last_synced_at
    ? `Last synced ${new Date(statusQuery.data.last_synced_at).toLocaleString()}`
    : "Not synced yet";

  const syncCategories = async () => {
    setSyncing(true);
    setSyncError("");
    const { data, error } = await supabase.functions.invoke("channel-taxonomy-sync", {
      body: { org_id: props.orgId, connector_id: props.connectorId },
    });
    setSyncing(false);
    if (error) {
      setSyncError(error.message);
      return;
    }
    if (!data?.success) {
      setSyncError(data?.error || "Category sync failed.");
      return;
    }
    await queryClient.invalidateQueries({ queryKey: ["channel-taxonomy-status", props.orgId, props.connectorId] });
    await queryClient.invalidateQueries({ queryKey: ["channel-taxonomy-search"] });
    await queryClient.invalidateQueries({ queryKey: ["channel-taxonomy-children"] });
    await queryClient.invalidateQueries({ queryKey: ["channel-taxonomy-hints"] });
  };

  return (
    <div className="space-y-2" data-testid={`category-picker-${props.connectorId}`}>
      <label className="block text-xs font-medium text-slate-500">Channel category</label>
      {props.selectedId ? (
        <div className="flex items-start justify-between gap-2 px-3 py-2 bg-emerald-50 border border-emerald-100 rounded-lg">
          <div>
            <p className="text-sm text-slate-800">{props.selectedName || "Selected category"}</p>
            <p className="text-xs text-slate-400">{props.confirmed ? "Provider ID stored internally" : "Unconfirmed"}</p>
          </div>
          <button type="button" onClick={props.onClear} className="text-xs text-slate-500 hover:text-slate-800">Clear</button>
        </div>
      ) : (
        <p className="text-xs text-slate-400">Search or browse by name. Provider IDs stay internal until you select a row.</p>
      )}
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        className={inputCls}
        placeholder={`Search ${providerLabel(provider) || "channel"} categories`}
        disabled={!cacheReady || count === 0}
        data-testid={`category-search-${props.connectorId}`}
      />
      {!cacheReady && (
        <p className="text-xs text-slate-500">Category search will use this provider’s cache once a connector adapter is connected. Do not paste guessed IDs.</p>
      )}
      {statusQuery.isError && (
        <p className="text-xs text-red-700">Could not load category cache: {(statusQuery.error as Error).message}</p>
      )}
      {statusQuery.isFetching && <p className="text-xs text-slate-500">Checking cached categories…</p>}
      {cacheReady && !statusQuery.isFetching && count > 0 && (
        <p className="text-[11px] text-slate-400">
          {count.toLocaleString()} cached categories · {staleLabel}
          {statusQuery.data?.shared_cache ? " · using shared eBay AU taxonomy cache" : ""}
        </p>
      )}
      {empty && (
        <div className="space-y-2">
          <p className="text-xs text-amber-700">
            No cached categories for this connector. Sync the real {providerLabel(provider)} taxonomy — categories are not invented here.
          </p>
          <button
            type="button"
            onClick={() => void syncCategories()}
            disabled={syncing}
            className="px-2 py-1 text-xs rounded-lg border border-emerald-200 text-emerald-800 bg-emerald-50 disabled:opacity-60"
            data-testid={`category-sync-${props.connectorId}`}
          >
            {syncing ? "Syncing categories…" : "Sync categories"}
          </button>
        </div>
      )}
      {count > 0 && (
        <button
          type="button"
          onClick={() => void syncCategories()}
          disabled={syncing}
          className="text-xs font-medium text-slate-500 hover:text-slate-800"
        >
          {syncing ? "Refreshing…" : "Refresh categories"}
        </button>
      )}
      {syncError && <p className="text-xs text-red-700">{syncError}</p>}
      {(hintResults.data ?? []).length > 0 && !query.trim() && (
        <div>
          <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">Suggested from product data — confirm to use</p>
          <div className="flex flex-col gap-1">
            {hintResults.data!.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => props.onSelect(c.id, categoryHierarchyLabel(c))}
                className="text-left px-2 py-1.5 text-xs rounded-lg border border-slate-200 hover:border-emerald-400 hover:bg-emerald-50"
              >
                {categoryHierarchyLabel(c)}
              </button>
            ))}
          </div>
        </div>
      )}
      {query.trim().length >= 2 && searchQuery.isFetching && <p className="text-xs text-slate-500">Searching…</p>}
      {query.trim().length >= 2 && (searchQuery.data?.length ?? 0) > 0 && (
        <ul className="max-h-40 overflow-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
          {searchQuery.data!.map((c) => (
            <li key={c.id}>
              <button type="button" onClick={() => props.onSelect(c.id, categoryHierarchyLabel(c))} className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50">
                <span className="block text-slate-800">{c.name}</span>
                <span className="block text-slate-400">{categoryHierarchyLabel(c)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {query.trim().length >= 2 && !searchQuery.isFetching && (searchQuery.data?.length ?? 0) === 0 && count > 0 && (
        <p className="text-xs text-slate-500">No cached category matches “{query}”.</p>
      )}
      <button
        type="button"
        onClick={() => setBrowse((v) => !v)}
        className="text-xs font-medium text-emerald-700 disabled:text-slate-400"
        disabled={!cacheReady || count === 0}
      >
        {browse ? "Hide browse" : "Browse categories"}
      </button>
      {browse && count > 0 && (
        <div className="border border-slate-200 rounded-lg">
          <div className="px-3 py-2 text-[11px] text-slate-500 border-b border-slate-100">
            {browseStack.map((crumb, i) => (
              <button
                key={`${crumb.id ?? "root"}-${i}`}
                type="button"
                className="hover:text-emerald-700"
                onClick={() => setBrowseStack(browseStack.slice(0, i + 1))}
              >
                {i > 0 ? " / " : ""}
                {crumb.name}
              </button>
            ))}
          </div>
          {childrenQuery.isFetching && <p className="px-3 py-2 text-xs text-slate-500">Loading…</p>}
          <ul className="max-h-48 overflow-auto divide-y divide-slate-100">
            {(childrenQuery.data ?? []).map((c) => (
              <li key={`browse-${c.id}`} className="flex items-stretch">
                <button type="button" onClick={() => props.onSelect(c.id, categoryHierarchyLabel(c))} className="flex-1 text-left px-3 py-2 text-xs hover:bg-slate-50">
                  {c.name}
                </button>
                {!c.isLeaf && (
                  <button
                    type="button"
                    className="px-2 text-xs text-emerald-700"
                    onClick={() => setBrowseStack((s) => [...s, { id: c.id, name: c.name }])}
                  >
                    Open
                  </button>
                )}
              </li>
            ))}
          </ul>
          {!childrenQuery.isFetching && (childrenQuery.data?.length ?? 0) === 0 && (
            <p className="px-3 py-2 text-xs text-slate-500">No categories at this level.</p>
          )}
        </div>
      )}
    </div>
  );
}
